
import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { usePartnerPmsContext } from '../../contexts/PartnerPmsContext';
import { ActionButton } from '../Button';
import SelectField from '../SelectField';
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, RoomStatusData, RoomStatusEnum } from '../../data/Enums';
import Drawer, { IDefaultDrawerProps } from '../Drawer';
import Switch from '../Switch';
import { useAuthContext } from '../../contexts/AuthContext';
import { IBlock, IFloor, IRoomType } from '../../interfaces/IPartnerPms';
import useState from '../../hooks/useState';


interface IFilter {
    occupancyStatus: OccupancyStatusEnum[];
    governanceStatus: GovernanceStatusEnum[];
    roomStatus: RoomStatusEnum[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    onlyUserRooms: boolean;
}


const Filters: React.FC<IDefaultDrawerProps> = ({
    open,
    handleClose
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();
    const { 
        selectedOccupancyStatus,
        selectedGovernanceStatus,
        selectedRoomStatus,
        selectedBlock,
        selectedFloor,
        selectedRoomType, 
        hasUserRooms, 
        onlyUserRooms, 
        handleChangeSelectedFilters 
    } = usePartnerPmsContext();


    const state = useState<IFilter>({
        onlyUserRooms: onlyUserRooms,
        occupancyStatus: selectedOccupancyStatus,
        governanceStatus: selectedGovernanceStatus,
        roomStatus: selectedRoomStatus,
        block: selectedBlock,
        floor: selectedFloor,
        roomType: selectedRoomType
    });


    const enterprisePartnerPms = useMemo(() => {
        return {
            blocks: enterprise?.partnerPms.blocks ?? [],
            floors: enterprise?.partnerPms.floors ?? [],
            roomTypes: enterprise?.partnerPms.roomTypes ?? [],
            hasUserRooms: (enterprise?.partnerPms.userRooms ?? []).length > 0
        }
    }, [enterprise]);


    const _handleApplyFilters = useCallback((filters: IFilter) => {
        handleChangeSelectedFilters( 
            filters.occupancyStatus, 
            filters.governanceStatus, 
            filters.roomStatus,
            filters.block, 
            filters.floor, 
            filters.roomType,
            filters.onlyUserRooms,
        );
        handleClose();
        // eslint-disable-next-line
    }, []);


    const footer = useMemo(() => {
        return <ActionButton
            action='apply'
            fullWidth={true}
            className='text-alwaysshow'
            onClick={() => _handleApplyFilters(state.values)}
        />;
        // eslint-disable-next-line
    }, [state.values, translate]);
    

    return (
        <Drawer
            anchor='right'
            open={open}
            handleClose={handleClose}
            title={translate('filter')}
            footer={footer}
        >
            <Grid container>
                <Grid item xs={12}>
                    <SelectField
                        label={translate('occupancyStatus')}
                        name='occupancyStatus'
                        value={state.values.occupancyStatus.length > 0 ? state.values.occupancyStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const occupancyStatus = state.values.occupancyStatus.length > 0 && values.includes(-1) ? [] : Object.values(OccupancyStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, occupancyStatus);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('governanceStatus')}
                        name='governanceStatus'
                        value={state.values.governanceStatus.length > 0 ? state.values.governanceStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const governanceStatus = state.values.governanceStatus.length > 0 && values.includes(-1) ? [] : Object.values(GovernanceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, governanceStatus);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('roomStatus')}
                        name='roomStatus'
                        value={state.values.roomStatus.length > 0 ? state.values.roomStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomStatus = state.values.roomStatus.length > 0 && values.includes(-1) ? [] : Object.values(RoomStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, roomStatus);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(RoomStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={state.values.block.length > 0 ? state.values.block.map(b => +b.id) : [-1]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = state.values.block.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            state.update(name, block);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={state.values.floor.length > 0 ? state.values.floor.map(f => +f.id) : [-1]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = state.values.floor.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            state.update(name, floor);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={state.values.roomType.length > 0 ? state.values.roomType.map(r => +r.id) : [-1]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = state.values.roomType.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            state.update(name, roomType);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                {
                    hasUserRooms && <Grid item xs={12} marginTop={2} className='textalign-center'>
                        <Switch
                            label={translate('_showOnlyRoomsAssignedToMe')}
                            labelPlacement='bottom'
                            name='onlyUserRooms'
                            value={state.values.onlyUserRooms ? 0 : 1}
                            checked={state.values.onlyUserRooms}
                            onChange={e => state.update(e.target.name, Boolean(+e.target.value))}
                        />
                    </Grid>
                }
            </Grid>
        </Drawer>
    );
}

export default Filters;